import {
  BaseListItem
} from "./chunk.LBF6MTVU.js";
import {
  emit
} from "./chunk.HM4ZRC4T.js";
import {
  watch
} from "./chunk.HBM5K2JV.js";
import {
  __decorateClass,
  component_styles_default,
  theme_styles_default
} from "./chunk.U4VUMZVF.js";

// src/components/list/list.ts
import { LitElement, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";

// src/components/list/list.styles.ts
import { css } from "lit";
var list_styles_default = css`
	${theme_styles_default}
	${component_styles_default}

	:host {
		--height: 100%;
		--width: 100%;

		display: block;
		height: var(--height);
		width: var(--width);
		box-sizing: border-box !important;
	}
`;

// src/components/list/list.ts
var YOTPO_LIST_TAG_NAME = "yotpo-list";
var averageItemHeight = 36;
var YotpoList = class extends LitElement {
  constructor() {
    super(...arguments);
    this.typeToSelectString = "";
    this.multiSelectable = false;
    this.radio = false;
    this.selectionByTyping = true;
    this.numberOfItems = 6;
    this.fontList = false;
    this.handleRequestChange = (event) => {
      event.stopPropagation();
      const item = this.getTargetClosestListItem(event);
      if (!item || item.disabled)
        return;
      if (item.checked && !this.multiSelectable) {
        if (this.isInDropdown) {
          emit(this, "yotpo-hide" /* hide */, { detail: this });
          return;
        }
      } else {
        item.setChecked(!item.checked, { emitEvent: false });
      }
      this.handleItemSelected(item);
    };
    this.handleKeyDown = (event) => {
      event.stopPropagation();
      if (event.key === "Enter") {
        this.handleRequestChange(event);
        return;
      }
      if (event.key === " ") {
        event.preventDefault();
        return;
      }
      if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
        event.preventDefault();
        this.handleNavigationKeyboardClick(event);
        return;
      }
      if (this.selectionByTyping)
        this.typeToSelect(event);
    };
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keydown", this.handleKeyDown);
    this.addEventListener("yotpo-request-change" /* requestChange */, this.handleRequestChange);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keydown", this.handleKeyDown);
    this.removeEventListener("yotpo-request-change" /* requestChange */, this.handleRequestChange);
  }
  getAllItems(options = { includeDisabled: true }) {
    var _a;
    return (_a = this.defaultSlot) == null ? void 0 : _a.assignedElements({ flatten: true }).filter((el) => {
      if (!(el instanceof BaseListItem))
        return false;
      if (!options.includeDisabled && el.disabled)
        return false;
      return true;
    });
  }
  setAllItemsSelectionType() {
    const items = this.getAllItems({ includeDisabled: false });
    items.forEach((listItem) => {
      listItem.multiSelectable = this.multiSelectable;
    });
  }
  setAllItemsRadio() {
    if (this.multiSelectable) {
      if (this.radio) {
        console.error("Multi-Selectable list can't have radios");
      }
      return;
    }
    const items = this.getAllItems({ includeDisabled: false });
    items.forEach((listItem) => {
      listItem.radio = this.radio;
    });
  }
  deselectAllOtherItems(item) {
    const items = this.getAllItems({ includeDisabled: false });
    items.forEach((listItem) => {
      if (listItem !== item) {
        listItem.setChecked(false, { emitEvent: false });
        listItem.setAttribute("tabindex", "-1");
      }
    });
  }
  getCurrentItem() {
    return this.getAllItems({ includeDisabled: false }).find((i) => i.getAttribute("tabindex") === "0");
  }
  setCurrentItem(item) {
    const items = this.getAllItems({ includeDisabled: false });
    const activeItem = item.disabled ? items[0] : item;
    items.forEach((listItem) => {
      listItem.setAttribute("tabindex", listItem === activeItem ? "0" : "-1");
    });
  }
  removeCurrentItem(item) {
    var _a;
    const items = this.getAllItems({ includeDisabled: false });
    (_a = items.find((listItem) => listItem === item)) == null ? void 0 : _a.setAttribute("tabindex", "-1");
  }
  clearSelection() {
    const items = this.getAllItems({ includeDisabled: false });
    items.forEach((listItem) => {
      if (listItem.checked) {
        listItem.setChecked(false, { emitEvent: false });
        listItem.setAttribute("tabindex", "-1");
      }
    });
  }
  async handleNumberOfItemsChange() {
    await this.updateComplete;
    requestAnimationFrame(() => {
      const items = this.getAllItems();
      if (items.length > this.numberOfItems)
        this.list.style.maxHeight = averageItemHeight * this.numberOfItems + "px";
    });
  }
  async handleMultiSelectableChange() {
    await this.updateComplete;
    this.setAllItemsSelectionType();
  }
  async handleRadioChange() {
    await this.updateComplete;
    this.setAllItemsRadio();
  }
  typeToSelect(event) {
    this.buildTypeToSelectString(event);
    this.selectItemBySelectString();
  }
  buildTypeToSelectString(event) {
    if (this.typeToSelectTimeout) {
      clearTimeout(this.typeToSelectTimeout);
    }
    this.typeToSelectTimeout = setTimeout(() => this.typeToSelectString = "", 1e3);
    if (event.key === "Backspace") {
      if (event.metaKey || event.ctrlKey) {
        this.typeToSelectString = "";
      } else {
        this.typeToSelectString = this.typeToSelectString.slice(0, -1);
      }
    } else {
      this.typeToSelectString += event.key.toLowerCase();
    }
  }
  selectItemBySelectString() {
    const items = this.getAllItems({ includeDisabled: false });
    if (!items)
      return;
    const mostFittingItem = items.find((item) => item.label.toLowerCase().startsWith(this.typeToSelectString));
    if (!mostFittingItem)
      return;
    mostFittingItem.focus();
  }
  handleItemSelected(item) {
    if (!item || item.disabled)
      return;
    if (item.checked) {
      if (!this.multiSelectable)
        this.deselectAllOtherItems(item);
      this.setCurrentItem(item);
      emit(this, "yotpo-select" /* select */, { detail: item });
    } else {
      this.removeCurrentItem(item);
    }
    const allSelectedItems = this.getAllItems({ includeDisabled: false }).filter(({ checked }) => checked);
    emit(this, "yotpo-change" /* change */, { detail: allSelectedItems });
  }
  get isInDropdown() {
    return !!this.closest("yotpo-dropdown");
  }
  handleNavigationKeyboardClick(event) {
    const items = this.getAllItems({ includeDisabled: false });
    if (items.length <= 0)
      return;
    const index = this.getIndexOfNextItem(items, event);
    this.setCurrentItem(items[index]);
    items[index].focus();
  }
  getIndexOfNextItem(items, event) {
    const activeItem = this.getTargetClosestListItem(event);
    let index = activeItem ? items.indexOf(activeItem) : 0;
    index = this.getNextIndexByKey(event.key, index, items.length);
    if (index < 0) {
      index = items.length - 1;
    }
    if (index > items.length - 1) {
      index = 0;
    }
    return index;
  }
  getNextIndexByKey(key, index, listLength) {
    switch (key) {
      case "ArrowDown":
        return index + 1;
      case "ArrowUp":
        return index - 1;
      case "Home":
        return 0;
      case "End":
        return listLength - 1;
      default:
        return index;
    }
  }
  getTargetClosestListItem(event) {
    const target = event.target;
    if (this.fontList) {
      return target.closest("yotpo-list-font-item");
    }
    return target.closest("yotpo-list-item");
  }
  handleSlotChange() {
    this.setAllItemsSelectionType();
    this.setAllItemsRadio();
    this.handleNumberOfItemsChange();
    emit(this, "slotchange");
  }
  render() {
    return html`
			<div part="base" class="list" aria-multiselectable=${this.multiSelectable ? "true" : "false"}>
				<slot @slotchange=${this.handleSlotChange}></slot>
			</div>
		`;
  }
};
YotpoList.styles = list_styles_default;
__decorateClass([
  query(".list")
], YotpoList.prototype, "list", 2);
__decorateClass([
  query("slot")
], YotpoList.prototype, "defaultSlot", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoList.prototype, "multiSelectable", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoList.prototype, "radio", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoList.prototype, "selectionByTyping", 2);
__decorateClass([
  property({ type: Number, reflect: true })
], YotpoList.prototype, "numberOfItems", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoList.prototype, "fontList", 2);
__decorateClass([
  watch("numberOfItems")
], YotpoList.prototype, "handleNumberOfItemsChange", 1);
__decorateClass([
  watch("multiSelectable")
], YotpoList.prototype, "handleMultiSelectableChange", 1);
__decorateClass([
  watch("radio")
], YotpoList.prototype, "handleRadioChange", 1);
YotpoList = __decorateClass([
  customElement(YOTPO_LIST_TAG_NAME)
], YotpoList);

export {
  YOTPO_LIST_TAG_NAME,
  YotpoList
};
